import classNames from 'classnames';
import type { Op } from 'quill/core';
import { Delta } from 'quill/core';
import React from 'react';
import * as styles from './CommentPreviewText.module.scss';
import {
  truncateMentionContent,
  TRUNCATION_LENGTHS,
} from '../../editor/mentions/MentionBase';
import { getAttachmentMentionText } from '../../editor/utils/deltaToText';
import getFirstName from '../../utils/text/getFirstName';

type CommentPreviewTextProps = {
  context: Delta | Op[];
} & (
  | {
      maxLines: 1;
      quoted?: boolean;
    }
  | {
      maxLines: 2 | 3;
      quoted?: never;
    }
);

const CommentPreviewText = ({
  context,
  quoted,
  maxLines,
}: CommentPreviewTextProps) => {
  if (!context?.length) {
    return null;
  }

  const delta = new Delta(context);
  return (
    <div
      className={classNames(styles.container, {
        [styles.isCommentQuote]: quoted,
      })}
    >
      <div className={styles.content} data-max-lines={maxLines}>
        {delta.ops.map((op, index) => {
          const key = index;
          if (typeof op.insert === 'string') {
            return op.insert;
          }
          if (
            typeof op.insert === 'object' &&
            typeof op.insert.mention === 'object' &&
            !!op.insert.mention
          ) {
            const optionalString = (value: unknown) =>
              typeof value === 'string' ? value : undefined;

            const mentionText =
              optionalString(op.attributes?.['mention-text']) ||
              optionalString(op.attributes?.content) ||
              '';

            const isUserMention =
              'type' in op.insert.mention && op.insert.mention.type === 'user';
            const content = isUserMention
              ? `@${getFirstName(mentionText) ?? ''}`
              : `+${mentionText}`;

            return (
              <span className={styles.mention} key={key}>
                {truncateMentionContent(content, TRUNCATION_LENGTHS.comment)}
              </span>
            );
          }
          const attachmentMentionText = getAttachmentMentionText(op);
          if (attachmentMentionText) {
            return (
              <span className={styles.mention} key={key}>
                +
                {truncateMentionContent(
                  attachmentMentionText,
                  TRUNCATION_LENGTHS.comment,
                )}
              </span>
            );
          }
          if (typeof op.attributes?.content === 'string') {
            return op.attributes.content;
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default CommentPreviewText;
